import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { usePageHeader } from "../../../contexts/PageHeaderContext";
import { Button, Breadcrumb } from "../../../components/ui";
import {
  previewCorrection,
  employeeAmount,
  employeePercentage,
  changeEmployee,
  changeSubTeam,
  n,
} from "./correctionUtils";
import "./Corrections.css";
const money = (value) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(
    n(value),
  ) + " ₽";
const pct = (value) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 4 }).format(n(value));
const periodDate = (v) =>
  new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString("ru-RU");
const period = (g) =>
  `${periodDate(g.valid_from)} — ${g.valid_to ? periodDate(g.valid_to) : "по настоящее время"}`;
const Field = ({ label, children, wide }) => (
  <label className={`cw-field ${wide ? "cw-wide" : ""}`}>
    <span>{label}</span>
    {children}
  </label>
);
const Numeric = ({ label, value, onChange, disabled, leading }) => (
  <input
    aria-label={label}
    className={leading ? "cw-leading" : ""}
    inputMode="decimal"
    type="number"
    min="0"
    step="any"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
  />
);
export default function CorrectionWizard() {
  const { id, correctionId } = useParams(),
    navigate = useNavigate();
  usePageHeader({ title: null, hideTopHeader: true }, []);
  const [ctx, setCtx] = useState(null),
    [form, setForm] = useState(null),
    [step, setStep] = useState(correctionId ? 2 : 1);
  const [deps, setDeps] = useState([]),
    [services, setServices] = useState([]),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false),
    [adding, setAdding] = useState(null),
    [search, setSearch] = useState("");
  const [externalTeam, setExternalTeam] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const saved = correctionId
          ? (await api.get(`/company/corrections/${correctionId}`)).data
          : null;
        const p = saved?.custom_data?.correction;
        const c = (
          await api.get(
            `/company/corrections/context/${p?.object_stage_id || id}`,
            { params: p ? { group_id: p.group_id } : {} },
          )
        ).data;
        if (!active) return;
        setCtx(c);
        setForm(
          p
            ? { ...p }
            : {
                name: `Корр-ка ${new Date().toLocaleDateString("ru-RU")}`,
                description: "",
                contract_id: c.contracts[0]?.id || "",
                group_id: c.group_id,
                status: "в работе",
                planned_date: "",
                fact_date: "",
                completion_date: "",
              },
        );
        setDeps(p ? p.departments : []);
        setServices(
          p
            ? p.services
            : (c.services || []).map((r) => ({
                ...r,
                base_percentage: r.percentage,
                leading: "percentage",
              })),
        );
      } catch (e) {
        if (active)
          setError(e.response?.data?.error || "Не удалось загрузить мастер");
      }
    })();
    return () => {
      active = false;
    };
  }, [id, correctionId]);
  if (!ctx || !form)
    return (
      <div className="company-page">
        {error ? <p role="alert">{error}</p> : "Загрузка мастера…"}
      </div>
    );
  const preview = previewCorrection(deps, services, ctx.vat_percent);
  const contract = ctx.contracts.find((c) => c.id === form.contract_id);
  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const backUrl = `/company/budgets/${ctx.stage.id}`;
  const updateDep = (index, fn) =>
    setDeps((ds) => ds.map((d, i) => (i === index ? fn(d) : d)));
  const changeGroup = async (value) => {
    if (
      (deps.length || correctionId) &&
      !window.confirm(
        "Сменить период? Правки исполнителей и обслуживания будут сброшены.",
      )
    )
      return;
    try {
      const c = (
        await api.get(`/company/corrections/context/${ctx.stage.id}`, {
          params: { group_id: value },
        })
      ).data;
      setCtx(c);
      updateForm("group_id", value);
      setDeps([]);
      setServices(
        (c.services || []).map((r) => ({
          ...r,
          base_percentage: r.percentage,
          leading: "percentage",
        })),
      );
      setError("");
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сменить период");
    }
  };
  const toggleDepartment = (d) =>
    setDeps((ds) =>
      ds.some((x) => x.id === d.id)
        ? ds.filter((x) => x.id !== d.id)
        : [...ds, structuredClone(d)],
    );
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        object_stage_id: ctx.stage.id,
        departments: deps,
        services,
      };
      const res = correctionId
        ? await api.put(`/company/corrections/${correctionId}`, payload)
        : await api.post("/company/corrections", payload);
      navigate(`/company/corrections/${res.data.public_id}`, {
        state: { created: !correctionId },
      });
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сохранить корректировку");
    } finally {
      setSaving(false);
    }
  };
  const basicsValid =
    form.name.trim() &&
    form.contract_id &&
    form.description.length <= 2000 &&
    ctx.services !== null;
  return (
    <div className="company-page cw-page">
      <Breadcrumb
        items={[
          { label: "Бюджеты", to: "/company/budgets" },
          {
            label: ctx.stage.object_name,
            to: `/object/${ctx.stage.object_id}`,
          },
          { label: ctx.stage.name, to: backUrl },
          {
            label: correctionId
              ? "Изменить корректировку"
              : "Новая корректировка",
          },
        ]}
      />
      <header className="cw-header">
        <div>
          <h1>
            <button
              className="cw-back"
              onClick={() => navigate(backUrl)}
              aria-label="Назад к бюджету"
            >
              ←
            </button>
            {correctionId ? "Изменить корректировку" : "Новая корректировка"}
          </h1>
          <p>
            Этап «{ctx.stage.name}» · Объект {ctx.stage.object_name}
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate(backUrl)}>
          Отмена
        </Button>
      </header>
      <nav className="cw-steps" aria-label="Шаги мастера">
        {["Базовая информация", "Исполнители", "Итог"].map((label, i) => (
          <button
            key={label}
            className={step === i + 1 ? "active" : ""}
            disabled={i + 1 > step}
            onClick={() => setStep(i + 1)}
          >
            <b>{i + 1}</b>
            {label}
          </button>
        ))}
      </nav>
      {error && (
        <div role="alert" className="cw-error">
          {error}
        </div>
      )}
      <div className={`cw-layout ${step === 1 ? "cw-layout-basic" : ""}`}>
        <main>
          {step === 1 && (
            <section className="cw-card">
              <h2>Основа стадии</h2>
              <div className="cw-fields">
                <Field label="Этап объекта">
                  <input
                    readOnly
                    value={`${ctx.stage.name} — ${ctx.stage.object_name}`}
                  />
                </Field>
                <Field label="Договор *">
                  <select
                    value={form.contract_id}
                    disabled={!!correctionId}
                    onChange={(e) => updateForm("contract_id", e.target.value)}
                  >
                    <option value="">Выберите договор</option>
                    {ctx.contracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.number} · {c.status || "без статуса"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field wide label="Название корректировки *">
                  <input
                    maxLength={140}
                    value={form.name}
                    onChange={(e) => updateForm("name", e.target.value)}
                  />
                  <small>
                    Идентификатор стадии:{" "}
                    <strong>
                      {contract?.number} - {form.name}
                    </strong>
                  </small>
                </Field>
                <Field wide label="Что корректируем">
                  <textarea
                    rows={3}
                    maxLength={2000}
                    value={form.description}
                    onChange={(e) => updateForm("description", e.target.value)}
                  />
                </Field>
                <Field wide label="Группа базовых процентов">
                  <select
                    value={form.group_id}
                    onChange={(e) => changeGroup(e.target.value)}
                  >
                    {ctx.groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {period(g)}
                      </option>
                    ))}
                  </select>
                  {ctx.services === null && (
                    <small role="alert">
                      Настройте обслуживание в разделе «Базовые проценты».
                    </small>
                  )}
                </Field>
                {[
                  ["planned_date", "Плановая дата"],
                  ["fact_date", "Срок по договору"],
                  ["completion_date", "Дата завершения"],
                ].map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input
                      type="date"
                      value={form[key] || ""}
                      onChange={(e) => updateForm(key, e.target.value)}
                    />
                  </Field>
                ))}
                <Field label="Статус">
                  <select
                    value={form.status}
                    onChange={(e) => updateForm("status", e.target.value)}
                  >
                    {[
                      "не в работе",
                      "в работе",
                      "работы завершены",
                      "закрыта",
                    ].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <footer>
                <Button
                  variant="primary"
                  disabled={!basicsValid}
                  onClick={() => setStep(2)}
                >
                  Далее: исполнители →
                </Button>
              </footer>
            </section>
          )}
          {step === 2 && (
            <>
              <div className="cw-selection">
                <Button variant="ghost" onClick={() => setDeps([])}>
                  Снять все
                </Button>
              </div>
              {ctx.departments.map((base) => {
                const index = deps.findIndex((d) => d.id === base.id),
                  dep = deps[index];
                const total =
                  dep?.employees.reduce(
                    (s, e) => s + employeePercentage(dep, e),
                    0,
                  ) || 0;
                const teams = dep
                  ? [
                      ...new Set(
                        dep.employees.map((e) =>
                          JSON.stringify([e.team, e.sub_team || ""]),
                        ),
                      ),
                    ].map((s) => JSON.parse(s))
                  : [];
                return (
                  <section
                    className={`cw-card cw-department ${dep && preview.departmentErrors.includes(dep) ? "cw-invalid" : ""}`}
                    key={base.id}
                  >
                    <header>
                      <label className="cw-check">
                        <input
                          type="checkbox"
                          checked={!!dep}
                          onChange={() => toggleDepartment(base)}
                        />
                        <b>{base.name}</b>
                      </label>
                      {dep && (
                        <label className="cw-department-amount">
                          Сумма без НДС{" "}
                          <Numeric
                            label={`Сумма ${base.name} без НДС`}
                            value={dep.amount}
                            onChange={(v) =>
                              updateDep(index, (d) => ({ ...d, amount: v }))
                            }
                          />{" "}
                          ₽
                        </label>
                      )}
                    </header>
                    {dep && (
                      <>
                        <div className="cw-table-scroll">
                          <table>
                            <thead>
                              <tr>
                                <th></th>
                                <th>Исполнитель</th>
                                <th>Команда</th>
                                <th>% в подразделении</th>
                                <th>Сумма, ₽</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teams.map(([team, sub]) => (
                                <React.Fragment key={team + sub}>
                                  <tr className="cw-subteam">
                                    <td colSpan={3}>{sub || team}</td>
                                    <td>
                                      {pct(
                                        dep.employees
                                          .filter(
                                            (e) =>
                                              e.team === team &&
                                              (e.sub_team || "") === sub,
                                          )
                                          .reduce(
                                            (s, e) =>
                                              s + employeePercentage(dep, e),
                                            0,
                                          ),
                                      )}
                                      %
                                    </td>
                                    <td>
                                      <Numeric
                                        label={`Сумма ${base.name} ${sub || team}`}
                                        value={
                                          Math.round(
                                            dep.employees
                                              .filter(
                                                (e) =>
                                                  e.team === team &&
                                                  (e.sub_team || "") === sub,
                                              )
                                              .reduce(
                                                (s, e) =>
                                                  s + employeeAmount(dep, e),
                                                0,
                                              ) * 100,
                                          ) / 100
                                        }
                                        onChange={(v) =>
                                          updateDep(index, (d) =>
                                            changeSubTeam(d, team, sub, v),
                                          )
                                        }
                                      />
                                    </td>
                                  </tr>
                                  {dep.employees.map((e, ei) =>
                                    e.team !== team ||
                                    (e.sub_team || "") !== sub ? null : (
                                      <tr
                                        key={`${e.employee_id}-${team}-${sub}`}
                                        className={
                                          !e.enabled ? "cw-disabled" : ""
                                        }
                                      >
                                        <td>
                                          <input
                                            aria-label={`Участвует ${e.employee_name} ${sub || team}`}
                                            type="checkbox"
                                            checked={e.enabled}
                                            onChange={(v) =>
                                              updateDep(index, (d) => ({
                                                ...d,
                                                employees: d.employees.map(
                                                  (x, i) =>
                                                    i === ei
                                                      ? {
                                                          ...x,
                                                          enabled:
                                                            v.target.checked,
                                                        }
                                                      : x,
                                                ),
                                              }))
                                            }
                                          />
                                        </td>
                                        <td>
                                          <div className="cw-person">
                                            <span
                                              className="cw-avatar"
                                              aria-hidden="true"
                                            >
                                              {e.employee_name
                                                .split(/\s+/)
                                                .map((v) => v[0])
                                                .slice(0, 2)
                                                .join("")}
                                            </span>
                                            <div>
                                              <span>{e.employee_name}</span>
                                              <small>
                                                {e.external
                                                  ? "вне подразделения"
                                                  : `базовый ${pct(e.base_percentage)}%`}
                                              </small>
                                            </div>
                                          </div>
                                        </td>
                                        <td>{e.team}</td>
                                        <td>
                                          <Numeric
                                            label={`Доля ${e.employee_name} ${sub || team}`}
                                            disabled={!e.enabled}
                                            value={Number(
                                              employeePercentage(
                                                dep,
                                                e,
                                              ).toFixed(6),
                                            )}
                                            leading={e.leading === "percentage"}
                                            onChange={(v) =>
                                              updateDep(index, (d) =>
                                                changeEmployee(
                                                  d,
                                                  ei,
                                                  "percentage",
                                                  v,
                                                ),
                                              )
                                            }
                                          />
                                        </td>
                                        <td>
                                          <Numeric
                                            label={`Сумма ${e.employee_name} ${sub || team}`}
                                            disabled={!e.enabled}
                                            value={employeeAmount(dep, e)}
                                            leading={e.leading === "amount"}
                                            onChange={(v) =>
                                              updateDep(index, (d) =>
                                                changeEmployee(
                                                  d,
                                                  ei,
                                                  "amount",
                                                  v,
                                                ),
                                              )
                                            }
                                          />
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div
                          className={
                            Math.abs(total - 100) >= 0.01
                              ? "cw-error"
                              : "cw-dept-total"
                          }
                        >
                          Распределено {pct(total)}% ·{" "}
                          {money(
                            dep.employees.reduce(
                              (s, e) => s + employeeAmount(dep, e),
                              0,
                            ),
                          )}
                          {Math.abs(total - 100) >= 0.01 && " — требуется 100%"}
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setAdding(index);
                            setSearch("");
                            setExternalTeam(
                              JSON.stringify(teams[0] || ["", ""]),
                            );
                          }}
                        >
                          + Исполнитель не из подразделения
                        </Button>
                        {adding === index && (
                          <div className="cw-add-person">
                            <label>
                              Команда / подкоманда
                              <select
                                value={externalTeam}
                                onChange={(e) =>
                                  setExternalTeam(e.target.value)
                                }
                              >
                                {teams.map((t) => (
                                  <option
                                    key={JSON.stringify(t)}
                                    value={JSON.stringify(t)}
                                  >
                                    {t.filter(Boolean).join(" / ")}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <input
                              aria-label="Поиск исполнителя"
                              placeholder="ФИО — подразделение / команда"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />
                            <div className="cw-person-results">
                              {ctx.employees
                                .filter((e) =>
                                  e.full_name
                                    .toLocaleLowerCase()
                                    .includes(search.toLocaleLowerCase()),
                                )
                                .slice(0, 30)
                                .map((e) => (
                                  <button
                                    key={e.id}
                                    onClick={() => {
                                      const [team, sub] =
                                        JSON.parse(externalTeam);
                                      if (
                                        dep.employees.some(
                                          (x) =>
                                            x.employee_id === e.id &&
                                            x.team === team &&
                                            (x.sub_team || "") === sub,
                                        )
                                      ) {
                                        setError(
                                          "Этот сотрудник уже есть в выбранной команде",
                                        );
                                        return;
                                      }
                                      updateDep(index, (d) => ({
                                        ...d,
                                        employees: [
                                          ...d.employees,
                                          {
                                            employee_id: e.id,
                                            employee_name: e.full_name,
                                            team,
                                            sub_team: sub,
                                            enabled: true,
                                            external: true,
                                            percentage: 0,
                                            amount: 0,
                                            leading: "percentage",
                                          },
                                        ],
                                      }));
                                      setAdding(null);
                                      setError("");
                                    }}
                                  >
                                    {e.full_name} —{" "}
                                    {[e.department, e.team]
                                      .filter(Boolean)
                                      .join(" / ")}
                                  </button>
                                ))}
                            </div>
                            <Button
                              variant="ghost"
                              onClick={() => setAdding(null)}
                            >
                              Закрыть
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </section>
                );
              })}
              <footer className="cw-footer">
                <Button variant="secondary" onClick={() => setStep(1)}>
                  ← Назад
                </Button>
                <Button
                  variant="primary"
                  disabled={
                    !deps.length ||
                    !!preview.departmentErrors.length ||
                    preview.total <= 0
                  }
                  onClick={() => setStep(3)}
                >
                  Далее: итог →
                </Button>
              </footer>
            </>
          )}
          {step === 3 && (
            <section className="cw-card">
              <div className="cw-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Подразделение</th>
                      <th>Исполнитель</th>
                      <th>% распределения</th>
                      <th>По бюджету</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="cw-section">
                      <td colSpan={5}>
                        Производство · {pct(preview.productionPercent)}%
                      </td>
                    </tr>
                    {deps.flatMap((d) =>
                      d.employees
                        .filter((e) => e.enabled)
                        .map((e) => (
                          <tr
                            key={
                              d.id + e.employee_id + e.team + (e.sub_team || "")
                            }
                          >
                            <td>{d.name}</td>
                            <td>
                              {e.employee_name}
                              <small>
                                {[e.team, e.sub_team]
                                  .filter(Boolean)
                                  .join(" / ")}
                              </small>
                            </td>
                            <td>
                              {pct(
                                preview.total
                                  ? (employeeAmount(d, e) / preview.total) * 100
                                  : 0,
                              )}
                              %
                            </td>
                            <td>{money(employeeAmount(d, e))}</td>
                            <td />
                          </tr>
                        )),
                    )}
                    <tr className="cw-total">
                      <td colSpan={3}>Итого производство</td>
                      <td>{money(preview.production)}</td>
                      <td />
                    </tr>
                    <tr className="cw-section">
                      <td colSpan={5}>
                        Обслуживание · {pct(100 - preview.productionPercent)}%
                      </td>
                    </tr>
                    {preview.rows.map((r, i) => (
                      <tr
                        key={r.key}
                        className={r.removed ? "cw-disabled" : ""}
                      >
                        <td colSpan={2}>
                          {r.name}
                          {r.category === "tax" && !r.removed && (
                            <small>+ НДС {preview.vat}%</small>
                          )}
                        </td>
                        <td>
                          <Numeric
                            label={`Процент ${r.name}`}
                            disabled={r.removed}
                            leading={r.leading !== "amount"}
                            value={Number(r.calculatedPercentage.toFixed(6))}
                            onChange={(v) =>
                              setServices((rows) =>
                                rows.map((x, j) =>
                                  i === j
                                    ? {
                                        ...x,
                                        percentage: v,
                                        leading: "percentage",
                                      }
                                    : x,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <Numeric
                            label={`Сумма ${r.name}`}
                            disabled={r.removed}
                            leading={r.leading === "amount"}
                            value={r.calculatedAmount}
                            onChange={(v) =>
                              setServices((rows) =>
                                rows.map((x, j) =>
                                  i === j
                                    ? { ...x, amount: v, leading: "amount" }
                                    : x,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="cw-row-actions">
                          <button
                            aria-label={`Вернуть базовое значение ${r.name}`}
                            onClick={() =>
                              setServices((rows) =>
                                rows.map((x, j) =>
                                  i === j
                                    ? {
                                        ...x,
                                        percentage: x.base_percentage,
                                        leading: "percentage",
                                        removed: false,
                                      }
                                    : x,
                                ),
                              )
                            }
                          >
                            ↺
                          </button>
                          <button
                            aria-label={`Убрать ${r.name}`}
                            disabled={r.removed}
                            onClick={() =>
                              setServices((rows) =>
                                rows.map((x, j) =>
                                  i === j ? { ...x, removed: true } : x,
                                ),
                              )
                            }
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="cw-total">
                      <td colSpan={3}>Итого обслуживание</td>
                      <td>{money(preview.service)}</td>
                      <td />
                    </tr>
                    {Math.abs(preview.difference) >= 1 && (
                      <tr className="cw-error">
                        <td colSpan={3}>
                          {preview.difference > 0
                            ? "Не распределено по обслуживанию"
                            : "Превышение бюджета обслуживания"}
                        </td>
                        <td>{money(Math.abs(preview.difference))}</td>
                        <td />
                      </tr>
                    )}
                    <tr className="cw-grand">
                      <td colSpan={3}>Стоимость стадии без НДС</td>
                      <td>{money(preview.total)}</td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={3}>Стоимость стадии с НДС {preview.vat}%</td>
                      <td>{money(preview.gross)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              {!preview.valid && (
                <p role="alert" className="cw-error">
                  Проверьте распределение: суммы исполнителей и обслуживания
                  должны сходиться.
                </p>
              )}
              <footer>
                <Button variant="secondary" onClick={() => setStep(2)}>
                  ← Назад
                </Button>
                <Button
                  variant="primary"
                  disabled={!preview.valid || saving}
                  onClick={save}
                >
                  {saving
                    ? "Сохранение…"
                    : correctionId
                      ? "Сохранить распределение"
                      : "Создать корректировку"}
                </Button>
              </footer>
            </section>
          )}
        </main>
        {step > 1 && (
          <aside className="cw-summary">
            <div>
              <span>Производство</span>
              <strong>{money(preview.production)}</strong>
            </div>
            <div>
              <span>Обслуживание</span>
              <strong>{money(preview.service)}</strong>
            </div>
            <div>
              <span>Стоимость</span>
              <strong>{money(preview.total)}</strong>
              <small>с НДС: {money(preview.gross)}</small>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
